// The planning area's shell (spec 03 §4, D-354, D-390, D-450): the seven section
// tabs, the section's own pages where it has several, and the screen. Each
// screen's `PlanningHeader` carries the facility switcher in its actions. The
// shell reveals once the role feed and the facility list have both answered,
// so nothing it paints moves afterwards.
// Each entry opens on its own read code, so the shell renders for any reader
// holding one of them: maintenance, with `planning.calendar.read` alone, gets
// the navigation with the calendars entry and lands on it.

import { createContext, useContext, useEffect, useState } from "react";
import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { CardSkeleton, EmptyState, ErrorState, ForbiddenState } from "../../../components/states";
import { RouterTabs, type RouterTabDef } from "../../../components/tabs";
import { UnauthorizedError } from "../../../lib/problem";
import { requestIdOf } from "../../../lib/request";
import {
  planningAreaGroups,
  planningCodeFor,
  planningEntryFor,
  planningLandingPath,
  usePlanningPermissions,
} from "./hooks/permissions";
import { usePlanningFacilities, type Facility } from "./hooks/planning-queries";
import "./planning.css";

const FACILITY_KEY = "planning.facility";

interface PlanningFacilityValue {
  readonly facility: Facility;
  readonly facilities: readonly Facility[];
  readonly selectFacility: (id: string) => void;
}

const PlanningFacilityContext = createContext<PlanningFacilityValue | undefined>(undefined);

/** The facility every planning screen reads and writes. */
export function usePlanningFacility(): PlanningFacilityValue {
  const value = useContext(PlanningFacilityContext);
  if (value === undefined) throw new Error("usePlanningFacility outside the planning layout");
  return value;
}

function rememberedFacility(): string | undefined {
  try {
    return window.localStorage.getItem(FACILITY_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function rememberFacility(id: string): void {
  try {
    window.localStorage.setItem(FACILITY_KEY, id);
  } catch {
    // Storage may be blocked; the choice then lasts for this visit only.
  }
}

export function PlanningLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const perms = usePlanningPermissions();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const landing = planningLandingPath(perms);
  const canRead = landing !== undefined;
  // `/planning` is the workbench, which reads on `planning.read`; a reader who
  // holds another entry's code alone is sent to the first entry they hold.
  const atBareIndex = pathname === "/planning" || pathname === "/planning/";
  const redirecting =
    perms.isResolved && canRead && atBareIndex && !perms.has(planningCodeFor(pathname));
  const facilities = usePlanningFacilities();
  const [chosen, setChosen] = useState<string | undefined>(() => rememberedFacility());

  useEffect(() => {
    if (facilities.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [facilities.error, navigate]);

  const redirectTo = redirecting ? landing : undefined;
  useEffect(() => {
    if (redirectTo !== undefined) void navigate({ to: redirectTo, replace: true });
  }, [redirectTo, navigate]);

  if (!perms.isResolved || redirecting || (canRead && facilities.isPending)) {
    return (
      <div className="planning-area" key="planning-loading" data-testid="planning-loading">
        <div className="ds-tabs planning-subnav-skel" aria-hidden="true" />
        <CardSkeleton lines={6} label={t("planning.common.loading")} />
      </div>
    );
  }

  if (!canRead) {
    return (
      <div className="planning-area" key="planning-refused">
        <ForbiddenState title={t("planning.access.title")} body={t("planning.access.area")} />
      </div>
    );
  }

  if (facilities.error !== null || facilities.data === undefined) {
    return (
      <div className="planning-area" key="planning-error">
        <ErrorState
          title={t("planning.facility.error")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void facilities.refetch()}
          requestId={requestIdOf(facilities.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      </div>
    );
  }

  const list = facilities.data.items;
  const facility = list.find((f) => f.id === chosen) ?? list[0];
  if (facility === undefined) {
    return (
      <div className="planning-area" key="planning-empty">
        <EmptyState
          title={t("planning.facility.noneTitle")}
          body={t("planning.facility.noneBody")}
        />
      </div>
    );
  }

  // A screen whose read code the reader lacks is refused in words, not loaded.
  const screenAllowed = perms.has(planningCodeFor(pathname));

  // Seven section tabs (D-450, D-474). A section's tab opens its first screen this
  // reader may open and stays lit on every screen of the section; a section
  // with more than one screen shows them as a second, smaller row.
  const groups = planningAreaGroups(perms);
  const tabs: RouterTabDef[] = groups.map((group) => {
    const [first, ...rest] = group.entries;
    const to = first?.to ?? "/planning";
    return {
      to,
      label: t(group.labelKey),
      "data-testid": `planning-nav-group-${group.key}`,
      ...(to === "/planning" ? { exact: true } : { activePaths: rest.map((e) => e.to) }),
    };
  });
  const current = planningEntryFor(pathname);
  const section = groups.find((group) => group.key === current?.group);
  const sectionTabs: RouterTabDef[] | undefined =
    section !== undefined && section.entries.length > 1
      ? section.entries.map((entry) => ({
          to: entry.to,
          label: t(entry.sectionLabelKey ?? entry.labelKey),
          "data-testid": entry.testId,
          exact: entry.to === "/planning/settings",
        }))
      : undefined;

  const value: PlanningFacilityValue = {
    facility,
    facilities: list,
    selectFacility: (id) => {
      rememberFacility(id);
      setChosen(id);
    },
  };

  return (
    <PlanningFacilityContext.Provider value={value}>
      <div className="planning-area" key="planning-ready">
        <RouterTabs tabs={tabs} label={t("planning.nav.aria")} data-testid="planning-subnav" />
        {sectionTabs !== undefined ? (
          <RouterTabs
            tabs={sectionTabs}
            label={t("planning.nav.sectionAria")}
            className="planning-section-nav"
            data-testid="planning-section-nav"
          />
        ) : null}
        <div className="planning-body">
          {screenAllowed ? (
            <Outlet />
          ) : (
            <ForbiddenState title={t("planning.access.title")} body={t("planning.access.screen")} />
          )}
        </div>
      </div>
    </PlanningFacilityContext.Provider>
  );
}
